import { Box, Container, Flex, Button, Icon, Text, Spinner } from '@chakra-ui/react';
import {
  AddListingBanner,
  Breadcrumb,
  CategoryCards,
  ErrorMessageBox,
  SponsorsCard,
  LoginPrompt,
  CoverLoader,
} from 'src/components/atoms';
import { HeroCard, Announcement } from 'src/components/molecules';
import { ListingAttributes, ListingsView, FeaturedListingsBar } from 'src/components/organisms';
import { Category, MarketplaceInfo, PaginatedListingsResponse, SiteAndId } from 'src/api/v1-api';
import { CONTAINER_MAX_WIDTH_1, CONTAINER_MAX_WIDTH_2 } from 'src/constants/ui';
import { useEffect, useState } from 'react';
import { AttributeSelectorProvider } from 'src/contexts/AttributeSelector';
import { useRouter } from 'next/router';
import { FiPlusCircle } from 'react-icons/fi';
import { useAuth } from 'src/utils/auth';
import { GetStaticPropsContext } from 'next';
import CloneCategoryMenuButton from 'src/components/molecules/admin/clone-category-menu-button';
import { NextSeo } from 'next-seo';
import { checkCategoryPermissionsViewListing } from 'src/utils/categoryUtils';
import { srcFromCDN } from 'src/utils/files';
import { LISTINGS_PER_PAGE, MAP_LISTINGS_PER_PAGE } from 'src/constants/marketplace';
import { getMarketplaceUrl } from 'src/utils/marketplaceUtils';
import CollectEmailsModule from 'src/components/atoms/collect-emails-module';
interface Props {
  category: Category;
  marketplace: MarketplaceInfo;
  listings: PaginatedListingsResponse;
}

const ViewCategory = ({ category, marketplace, listings }: Props) => {
  const router = useRouter();
  const { user, hasRoles, userLoaded } = useAuth();

  const [announcementOpened, setAnnouncementOpened] = useState(true);
  const [userChangedPriceRange, handleUserChangedPriceRange] = useState(false);
  const showAnnouncement = category?.functional?.announcement && announcementOpened;
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [permissonChecked, setPermissionChecked] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>('');

  const showAddListing = category?.functional?.show_create_listing_button;

  // Workaround for silencing _AutofillCallbackHandler error in Instagram browser
  useEffect(() => {
    // @ts-ignore
    window._AutofillCallbackHandler =
      // @ts-ignore
      window._AutofillCallbackHandler ||
      function () {
        // This function intentionally left blank
      };
  }, []);

  const inventoryDisplay = category?.functional?.inventory_display;

  useEffect(() => {
    const checkPermissions = async () => {
      const { hasPermission, errorMessage } = await checkCategoryPermissionsViewListing(
        category?.view_listing_roles,
        marketplace?.id,
        user,
        hasRoles,
      );
      setHasPermission(hasPermission);
      setErrorMessage(errorMessage);
      setPermissionChecked(true);
    };

    if (userLoaded) {
      checkPermissions();
    }
  }, [category, marketplace, user, userLoaded]);

  const restoreScroll = (url: string) => {
    const savedY = sessionStorage.getItem(`scrollPos:${url}`);
    if (savedY) {
      window.scrollTo(0, parseFloat(savedY));
    }
  };
  const [listingsViewMounted, setListingsViewMounted] = useState(false);
  useEffect(() => {
    if (listingsViewMounted) {
      restoreScroll(router.asPath);
    }
  }, [listingsViewMounted, router.asPath]);

  const marketplaceUrl = getMarketplaceUrl(marketplace);
  const categoryUrl = `${marketplaceUrl}/category/${category?.id}`;
  const categoryImage = category?.image ? srcFromCDN(category.image, 800) : '';

  if (!marketplace || !category) {
    return null;
  }

  return (
    <>
      <NextSeo
        title={`${category?.name} | ${marketplace?.name}`}
        description={category?.description ?? ''}
        canonical={categoryUrl}
        openGraph={{
          type: 'website',
          url: categoryUrl,
          title: category?.name,
          description: category?.description ?? '',
          images: [
            {
              url: categoryImage,
              width: 800,
              height: 600,
              alt: category?.name,
            },
          ],
          site_name: marketplace?.name ?? category?.name,
          siteName: marketplace?.name ?? category?.name,
        }}
        twitter={{
          site: categoryUrl,
          cardType: 'summary_large_image',
        }}
      />

      {router.isFallback || !userLoaded || !permissonChecked ? (
        // Show spinner or loading state
        <Box
          pt={8}
          pb={8}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner />
        </Box>
      ) : (
        <>
          {hasPermission ? (
            <>
              <AddListingBanner page="category" />
              {marketplace?.functional?.collect_email_module?.position === 'top' && (
                <CollectEmailsModule
                  page="category"
                  collect_email_module={marketplace?.functional?.collect_email_module}
                  marketplace={marketplace}
                />
              )}
              {showAnnouncement && (
                <Container maxW={CONTAINER_MAX_WIDTH_1} pt={{ base: '8px', lg: '12px' }}>
                  <Announcement
                    text={category?.functional?.announcement ?? ''}
                    onClose={() => setAnnouncementOpened(false)}
                  />
                </Container>
              )}
              <SponsorsCard category={category} />
              <Container
                maxW={CONTAINER_MAX_WIDTH_1}
                pt={{ base: 4, md: 4, lg: 6 }}
                display="flex"
                flexDirection="column"
              >
                {router.isFallback && <CoverLoader size="xl" />}

                <CloneCategoryMenuButton categoryId={category?.id} />

                {category &&
                  category?.functional &&
                  category?.functional?.hero_card &&
                  Object.keys(category?.functional?.hero_card).length > 0 && (
                    <Box pb={{ base: 4, md: 5, lg: 6 }}>
                      <HeroCard
                        title={category.functional.hero_card.hero_card_title}
                        description={category.functional.hero_card.hero_card_description}
                        imageURL={category.functional.hero_card.hero_card_image_url}
                        mobileImageURL={category.functional.hero_card.hero_card_mobile_image_url}
                        cta={{
                          label: category.functional.hero_card.hero_card_cta,
                          link: category.functional.hero_card.hero_card_link,
                        }}
                        text_box_opacity={category.functional.opacity}
                        isSimple={category.functional.hero_card?.hero_card_is_simple}
                      />
                    </Box>
                  )}
                <Flex justifyContent="flex-start" alignItems="center" w="full">
                  <Breadcrumb parent={category?.parent} current={category?.name} />
                  <Box py={{ base: 4, md: 5, lg: 6 }}>
                    {showAddListing && (
                      <Button
                        colorScheme="primary"
                        onClick={() => router.push(`/listing/create?category=${category?.id}`)}
                      >
                        <Icon as={FiPlusCircle} mr="2" />
                        {category?.functional?.text_overrides?.add_listing_btn || 'Add Listing'}
                      </Button>
                    )}
                  </Box>
                </Flex>

                <Flex w="full" direction="column" rowGap={{ base: 12, md: 14, lg: 16 }}>
                  <CategoryCards category={category} />
                  <Flex direction="column" gap={{ base: 3, md: 4, lg: 5 }}>
                    <AttributeSelectorProvider
                      attributes={category?.attributes ?? []}
                      useToggle={(category?.functional?.enabled_view_types ?? []).length > 1}
                      defaultViewType={category?.functional?.default_view_type}
                      maxPrice={
                        category?.functional?.filter_max_price ??
                        category?.functional?.computed_filter_max_price
                      }
                      sortOptions={category?.functional?.sort_options ?? []}
                    >
                      {category?.functional?.has_featured_bar && (
                        <FeaturedListingsBar category={category} />
                      )}
                      {inventoryDisplay && inventoryDisplay !== 'None' && (
                        <ListingAttributes
                          category={category}
                          handleUserChangedPriceRange={handleUserChangedPriceRange}
                        />
                      )}
                      <ListingsView
                        propListings={listings}
                        fetchUrl={`${process.env.NEXT_PUBLIC_API_HOST}/listings/category/${category?.id}`}
                        initialMapFetchUrl={`${process.env.NEXT_PUBLIC_API_HOST}/listings/map/${category?.id}/`}
                        inventoryDisplay={category?.functional?.inventory_display}
                        paginationStyle={category?.functional?.pagination_style}
                        resultsPerPage={
                          category?.functional?.results_per_page
                            ? category.functional.results_per_page
                            : LISTINGS_PER_PAGE
                        }
                        mapResultsPerPage={
                          category?.functional?.map_results_per_page
                            ? category.functional.map_results_per_page
                            : MAP_LISTINGS_PER_PAGE
                        }
                        category={category}
                        userChangedPriceRange={userChangedPriceRange}
                        onMountComplete={() => setListingsViewMounted(true)}
                      />
                    </AttributeSelectorProvider>
                  </Flex>
                </Flex>
              </Container>
            </>
          ) : user ? (
            <Container maxW={CONTAINER_MAX_WIDTH_2} mt={10}>
              <ErrorMessageBox>
                {errorMessage}
                &nbsp;You can add roles to your account from the&nbsp;
                <Button variant="link" onClick={() => router.push('/account/roles')}>
                  <Text as="span" textDecoration="underline">
                    Marketplace Roles
                  </Text>
                </Button>
                &nbsp;page.
              </ErrorMessageBox>
            </Container>
          ) : userLoaded ? (
            <Container maxW={CONTAINER_MAX_WIDTH_1} mt={10}>
              <LoginPrompt message="to view this category." showWithSignup={true} />
            </Container>
          ) : (
            // Show loading state while user is being loaded
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Spinner />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export async function getStaticProps(context: GetStaticPropsContext) {
  const hostname = context.params?.site;
  const id = context.params?.id;

  if (!hostname || !id) {
    return {
      notFound: true,
      revalidate: 1,
    };
  }

  try {
    const categoryResponse = await fetch(
      `${process.env.NEXT_PUBLIC_API_HOST}/category_with_listings/${id}/`,
    );

    if (!categoryResponse.ok) {
      return {
        notFound: true,
        revalidate: 1,
      };
    }

    const categoryPayload = await categoryResponse.json();

    let category = {};
    for (const key in categoryPayload) {
      if (key !== 'marketplace' && key !== 'listings') {
        category = { ...category, [key]: categoryPayload[key] };
      }
    }

    const marketplace = categoryPayload['marketplace'];
    let listings = categoryPayload['listings'];
    if (listings === undefined) {
      listings = {};
    }

    return {
      props: {
        category,
        marketplace,
        listings,
      },
      revalidate: 10,
    };
  } catch (error) {
    return {
      notFound: true,
      revalidate: 1,
    };
  }
}

export async function getStaticPaths() {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_HOST}/active_category_ids/`);
  const data = await res.json();

  const paths = data
    .filter(
      (item: SiteAndId) =>
        item.site !== null && item.site !== undefined && item.id !== null && item.id !== undefined,
    )
    .map((item: SiteAndId) => ({
      params: {
        site: data[item.site.toString()] ?? item.site.toString(),
        id: item.id.toString(),
      },
    }));

  return { paths, fallback: 'blocking' };
}

export default ViewCategory;
